import {
  needCombinePortModels,
  defaultCategories,
  defaultModels,
  defaultLinkSpeedDefinitions,
  defaultPortTypes,
  defaultEnableState,
  defaultRSTP,
} from "../const";
import { accessPolicyTypeDefinition } from "../../const";

export const syncPortLabels = (port) => {
  const opType = port?.operation?.type || "switching";
  const groupId = port?.operation?.aggregateGroupId;
  let formattedAggregateLabel = "-";
  if (opType === "aggregation") {
    formattedAggregateLabel = groupId != null ? `Aggregate #${groupId}` : "Aggregate";
  } else {
    formattedAggregateLabel = "-";
  }
  const formattedAggregateStatus = opType === "aggregation" ? "Enabled" : "Disabled";

  const accessPolicies = port?.accessPolicy?.enableState
    ? (accessPolicyTypeDefinition[port?.accessPolicy?.type]?.label ?? "-")
    : accessPolicyTypeDefinition.DISABLED.label;

  const formattedRSTPLabel = port.rstpState ? "Enabled" : "Disabled";
  const stpGuard = port.stpState ? "Enabled" : "Disabled";
  const formattedLBDStateLabel = port.lbdState ? "Enabled" : "Disabled";
  const formattedDDPStateLabel = port.ddpState ? "Enabled" : "Disabled";

  const portSchedule = port.isPortScheduleMode && port.portSchedule ? port.portSchedule : "-";
  const poeSchedule = port.isPoeScheduleMode && port.poeSchedule ? port.poeSchedule : "-";

  // mirror column:
  let mirror = "-";
  if (opType === "mirroring") {
    const mirrorModeLabel = port?.operation?.mirrorMode === "tx" ? "TX" : (port?.operation?.mirrorMode === "rx" ? "RX" : "Both");
    const mirrorPorts = port?.operation?.mirrorPort || [];
    mirror = mirrorPorts.length > 0 ? `${mirrorModeLabel} (Port ${mirrorPorts.join(", ")})` : mirrorModeLabel;
  }

  return {
    ...port,
    formattedAggregateLabel,
    formattedAggregateStatus,
    accessPolicies,
    formattedRSTPLabel,
    stpGuard,
    formattedLBDStateLabel,
    formattedDDPStateLabel,
    mirror,
    portSchedule,
    poeSchedule,
  };
};

export const getInitCategory = ({
  config
}) => {
  let categories = [];

  if (config) {
    categories = structuredClone(defaultCategories).map(category => {
      category.isSelected = category.value === config.deviceCategory;
      return category;
    });
  } else {
    categories = structuredClone(defaultCategories);
  }

  return categories;
}

export const getInitModels = ({
  models,
  selectedCategory,
  selectedConfig,
}) => {
  if (!selectedCategory) return [];
  const isEditMode = !!selectedConfig;

  const tmpModels = models
    .filter(model => model.deviceCategory === selectedCategory.value)
    .map(model => {

      const truncatedHWVer = model?.hwVer ? model.hwVer.replace(/\d+$/, "") : null;
      const truncatedHWVerWithSuffix = truncatedHWVer ? truncatedHWVer + "x" : null;
      const seriesName = truncatedHWVer ? `${model.name}/${truncatedHWVer}` : model.name;
      const label = `${model.name} (${truncatedHWVerWithSuffix ?? "-"})`;

      return {
        ...model,
        seriesName,
        truncatedHWVer,
        truncatedHWVerWithSuffix,
        value: `${model.name}${truncatedHWVer ?? ""}`,
        label,
        isSelected: isEditMode ? seriesName === selectedConfig.seriesName : false,
      }
    })

  const defaultModel = {
    ...defaultModels[0],
    isSelected: !isEditMode
  };

  return [defaultModel, ...tmpModels];
}

// portTypes 定義:
// 1: Copper(RJ45),
// 2: Fiber(SFP),
// 3: Combo(Copper 先畫),
// 4: Combo(SFP 先畫).
// 裝置固定硬體屬性, 與連線狀態無關.
const PORT_MEDIA_TYPE_CONFIG = {
  1: { isCopper: true, isFiber: false },
  2: { isCopper: false, isFiber: true },
  3: { isCopper: true, isFiber: true },
  4: { isCopper: true, isFiber: true },
};

export const getInitPortType = ({ portTypes, portIndex }) => {
  const portType = portTypes?.[portIndex - 1];
  const { isCopper = false, isFiber = false } = PORT_MEDIA_TYPE_CONFIG[portType] ?? {};

  return { isCopper, isFiber, isCombo: isCopper && isFiber };
}

export const getFlattenLinkSpeedList = ({ linkSpeedList }) => {
  const readableList = linkSpeedList.map(linkSpeed => {
    const { count, s: fiberList = [], t: copperList = [] } = linkSpeed;

    return {
      count,
      copperList,
      fiberList,
    }
  })

  // [ range1-range2, range2-range3, range3-range4, ... ].
  // e.g. [2, 3, 2] means [1-2, 3-5, 6-7].
  let rangeList = [];
  readableList.reduce((prevCount, linkSpeed) => {
    const range = prevCount + linkSpeed.count;
    rangeList.push(range);

    return range;
  }, 0);

  const totalPortCount = rangeList[rangeList.length - 1];

  // Spread the link speed by the range list.
  // e.g. [ linkSpeed-1, linkSpeed-1, linkSpeed-2, linkSpeed-2, linkSpeed-2, linkSpeed-3, ... ]
  const flattenLinkSpeedList = Array(totalPortCount).fill(null).map((_, index) => {
    const matchedIndex = rangeList.findIndex(range => index < range);
    return readableList[matchedIndex];
  })

  return {
    flattenLinkSpeedList
  }
}

export const getInitDefaultConfig = ({
  responseConfig
}) => {
  const {
    capabilities,
    "wired.port": wiredPort,
    seriesName,
  } = responseConfig;

  const {
    linkSpeed: linkSpeedList,
    portTypes
  } = capabilities;

  const [modelName, hwVer] = seriesName ? seriesName.split(/\//) : [];

  const { flattenLinkSpeedList } = getFlattenLinkSpeedList({ linkSpeedList });

  const isNeedCombineModel = needCombinePortModels.includes(modelName);
  const combinePortName = isNeedCombineModel ? wiredPort.map(port => port.portName).join("") : "";
  const combinePortIndex = isNeedCombineModel ? wiredPort.map(port => port.portIndex).join("/") : "";

  let initPortConfigs = wiredPort.map(port => {

    const {
      portIndex,
      speed,
      comboSfpSpeed,
      portType,
      poe,
      poePowerUsage,
      poeState,
      ddpState,
      lbdState,
      portTags,
    } = port;

    const rstpState = port.rstpState !== undefined ? port.rstpState : (port.rstp === true || port.rstp === "enabled" || port.rstp === "fiber" || port.rstp === "copper");
    const stpState = port.stpState !== undefined ? port.stpState : (port.stpGuard === "Enabled" || port.stpGuard === "enabled" || !!port.stpState);
    const isPortScheduleMode = port.isPortScheduleMode !== undefined ? port.isPortScheduleMode : (!!port.portSchedule && port.portSchedule !== "-");
    const isPoeScheduleMode = port.isPoeScheduleMode !== undefined ? port.isPoeScheduleMode : (!!port.poeSchedule && port.poeSchedule !== "-");

    const operation = port.operation || {
      type: "switching",
      mirrorPort: [],
      mirrorMode: "",
      aggregatePort: [],
      aggregateType: "",
      aggregateGroupId: null
    };

    const accessPolicy = port.accessPolicy || {
      enableState: false,
      type: "STATIC",
      macList: null,
      radiusProfile: null,
      dynamicSize: null
    };

    const formattedFullName = (seriesName ?? "") + "/" + portIndex;
    const formattedPortType = defaultPortTypes[portType] ?? null;
    const formattedPoEState = defaultEnableState[poeState] ?? null;
    const formattedPoEUsageLabel = poeState !== true || (poe && parseInt(poePowerUsage, 10) < 0) ? "-" : poePowerUsage;
    const formattedPortTagsLabel = portTags?.length > 0 ? portTags?.join(", ") : "-";

    // speed
    const { isCopper, isFiber, isCombo } = getInitPortType({ portTypes, portIndex });
    const formattedCoperSpeed = defaultLinkSpeedDefinitions[speed] ?? null;
    const formattedFiberSpeed = defaultLinkSpeedDefinitions[isCombo ? comboSfpSpeed : speed] ?? null;
    const formattedFinalSpeed = isCopper ? formattedCoperSpeed : (isFiber ? formattedFiberSpeed : null);

    return syncPortLabels({
      ...port,
      rstpState,
      stpState,
      isPortScheduleMode,
      isPoeScheduleMode,
      operation,
      accessPolicy,
      formattedFullName,
      seriesName,
      modelName,
      hwVer,
      portTypes,
      poePorts: capabilities?.poePorts || responseConfig?.poePorts,
      aggregation: capabilities?.aggregation,
      supportedLinkSpeedList: flattenLinkSpeedList[portIndex - 1],

      formattedPortType,
      formattedPortTypeLabel: formattedPortType?.label ?? "-",

      formattedPoEState,
      formattedPoEStateLabel: formattedPoEState?.label ?? "-",

      formattedPoEUsageLabel,
      formattedPortTagsLabel,

      // speed
      isCopper,
      isFiber,
      formattedCoperSpeed,
      formattedCoperSpeedLabel: formattedCoperSpeed?.label ?? "-",

      formattedFiberSpeed,
      formattedFiberSpeedLabel: formattedFiberSpeed?.label ?? "-",

      formattedFinalSpeed,
      formattedFinalSpeedLabel: formattedFinalSpeed?.label ?? "-",

      combinePortName,
      combinePortIndex,
      isNeedCombineModel,
      isShow: isNeedCombineModel ? portIndex === 1 : true
    })
  })

  return {
    ...responseConfig,
    portConfigs: initPortConfigs
  };
}

export const getInitSelectedConfig = ({
  responseConfig,
  selectedConfig,
}) => {

  const {
    portConfigs,
  } = selectedConfig;

  const {
    capabilities,
    seriesName,
    "wired.port": wiredPort,
  } = responseConfig;

  const {
    linkSpeed: linkSpeedList,
    portTypes
  } = capabilities;

  const { flattenLinkSpeedList } = getFlattenLinkSpeedList({ linkSpeedList });

  const [modelName, hwVer] = seriesName ? seriesName.split(/\//) : [];

  const isNeedCombineModel = needCombinePortModels.includes(modelName);

  // If the current device models need to be merged and all their portNames are different, need combine the portNames together.
  const isAllNameSame = portConfigs.every(item => item.portName === portConfigs[0].portName);
  const combinePortName = isNeedCombineModel ? ( isAllNameSame ? portConfigs[0].portName : portConfigs.map(port => port.portName).join("") ) : "";
  const combinePortIndex = isNeedCombineModel ? portConfigs.map(port => port.portIndex).join("/") : "";

  const initPortConfigs = portConfigs.map(port => {

    const selectedDeviceCategory = wiredPort?.[0]?.deviceCategory;

    const {
      portIndex,
      speed,
      comboSfpSpeed,
      portType,
      poe,
      poePowerUsage,
      poeState,
      ddpState,
      lbdState,
      portTags,
    } = port;

    const rstpState = port.rstpState !== undefined ? port.rstpState : (port.rstp === true || port.rstp === "enabled" || port.rstp === "fiber" || port.rstp === "copper");
    const stpState = port.stpState !== undefined ? port.stpState : (port.stpGuard === "Enabled" || port.stpGuard === "enabled" || !!port.stpState);
    const isPortScheduleMode = port.isPortScheduleMode !== undefined ? port.isPortScheduleMode : (!!port.portSchedule && port.portSchedule !== "-");
    const isPoeScheduleMode = port.isPoeScheduleMode !== undefined ? port.isPoeScheduleMode : (!!port.poeSchedule && port.poeSchedule !== "-");

    const operation = port.operation || {
      type: "switching",
      mirrorPort: [],
      mirrorMode: "",
      aggregatePort: [],
      aggregateType: "",
      aggregateGroupId: null
    };

    const accessPolicy = port.accessPolicy || {
      enableState: false,
      type: "STATIC",
      macList: null,
      radiusProfile: null,
      dynamicSize: null
    };

    const formattedFullName = (seriesName ?? "") + "/" + portIndex;
    const formattedPortType = defaultPortTypes[portType] ?? null;
    const formattedPoEState = defaultEnableState[poeState] ?? null;
    const formattedPoEUsageLabel = poeState !== true || (poe && parseInt(poePowerUsage, 10) < 0) ? "-" : poePowerUsage;
    const formattedPortTagsLabel = portTags?.length > 0 ? portTags?.join(", ") : "-";

    // speed
    const { isCopper, isFiber, isCombo } = getInitPortType({ portTypes, portIndex });
    const formattedCoperSpeed = defaultLinkSpeedDefinitions[speed] ?? null;
    const formattedFiberSpeed = defaultLinkSpeedDefinitions[isCombo ? comboSfpSpeed : speed] ?? null;
    const formattedFinalSpeed = isCopper ? formattedCoperSpeed : (isFiber ? formattedFiberSpeed : null);

    return syncPortLabels({
      ...port,
      rstpState,
      stpState,
      isPortScheduleMode,
      isPoeScheduleMode,
      operation,
      accessPolicy,
      formattedFullName,
      seriesName,
      modelName,
      hwVer,
      portTypes,
      poePorts: capabilities?.poePorts || responseConfig?.poePorts || selectedConfig?.poePorts,
      aggregation: capabilities?.aggregation,
      deviceCategory: selectedDeviceCategory,
      supportedLinkSpeedList: flattenLinkSpeedList[portIndex - 1],

      formattedPortType,
      formattedPortTypeLabel: formattedPortType?.label ?? "-",

      formattedPoEState,
      formattedPoEStateLabel: formattedPoEState?.label ?? "-",

      formattedPoEUsageLabel,
      formattedPortTagsLabel,

      // speed
      isCopper,
      isFiber,
      formattedCoperSpeed,
      formattedCoperSpeedLabel: formattedCoperSpeed?.label ?? "-",

      formattedFiberSpeed,
      formattedFiberSpeedLabel: formattedFiberSpeed?.label ?? "-",

      formattedFinalSpeed,
      formattedFinalSpeedLabel: formattedFinalSpeed?.label ?? "-",

      combinePortName,
      combinePortIndex,
      isNeedCombineModel,
      isShow: isNeedCombineModel ? portIndex === 1 : true,
    })
  })

  return {
    ...responseConfig,
    portConfigs: initPortConfigs
  };
}

export const getCleanPayload = ({
  defaultConfig,
  selectedConfig,
}) => {

  const isEditMode = !!selectedConfig;

  const {
    "wired.port": wiredPort,
    capabilities,
    ...restConfig
  } = defaultConfig;

  const {
    formattedActiveState,
    formattedModelName,
    formattedHwVer,
    formattedHwVerLabel,
    formattedPortCount,
    deviceCategory,
    ...payloadConfig
  } = restConfig;

  if (isEditMode) payloadConfig.id = selectedConfig.id;
  payloadConfig.activeState = !isEditMode ? true : selectedConfig.activeState;
  payloadConfig.portConfigs = payloadConfig.portConfigs.map(port => {

    const {
      seriesName,
      modelName,
      hwVer,
      portTypes,
      formattedPortType,
      formattedPortTypeLabel,
      formattedAggregate,
      formattedAggregateLabel,
      formattedFullName,
      formattedPoEState,
      formattedPoEStateLabel,
      formattedPoEUsageLabel,
      formattedRSTP,
      formattedRSTPLabel,
      formattedDDPState,
      formattedDDPStateLabel,
      formattedLBDState,
      formattedLBDStateLabel,
      formattedPortTagsLabel,
      isCopper,
      isFiber,
      supportedLinkSpeedList,
      formattedCoperSpeed,
      formattedCoperSpeedLabel,
      formattedFiberSpeed,
      formattedFiberSpeedLabel,
      formattedFinalSpeed,
      formattedFinalSpeedLabel,
      combinePortName,
      combinePortIndex,
      isNeedCombineModel,
      isShow,
      stpGuard,
      accessPolicies,
      mirror,
      ...necessaryPort
    } = port;

    return necessaryPort;
  })

  const payload = {
    data: payloadConfig
  };

  return payload;
}